import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
} from '@mui/material';
import { fetchSummary } from '../api/summary';
import { fetchDailyPnl } from '../api/autoTrade';
import SummaryCards from '../components/SummaryCards';
import EquityChart from '../components/EquityChart';
import { getPriceColor, formatPct, formatMoney, formatNumber, formatDateTime } from '../utils/format';
import type { InvestmentSummary, DailyPnl, StockSellDetail } from '../types';

/**
 * 投资总结页
 * 概览卡片 + 交易明细 + 净值曲线
 * 支持按时间段查看
 */
export default function SummaryPage() {
  const [period, setPeriod] = useState('all');
  const [summary, setSummary] = useState<InvestmentSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [dailyPnlList, setDailyPnlList] = useState<DailyPnl[]>([]);
  const [detailFilter, setDetailFilter] = useState<'all' | 'up' | 'down'>('all');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchSummary(period);
        setSummary(data);
      } catch (error) {
        console.error('获取总结失败:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [period]);

  useEffect(() => {
    fetchDailyPnl(30)
      .then(setDailyPnlList)
      .catch((e) => console.error('Failed to load daily P&L:', e));
  }, [summary]);

  const stockDetails = summary?.stockDetails ?? [];
  const filteredDetails =
    detailFilter === 'all' ? stockDetails : stockDetails.filter((d) => d.sellType === detailFilter);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          投资总结
        </Typography>
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={(_e, val) => val && setPeriod(val)}
          size="small"
        >
          <ToggleButton value="today">今日</ToggleButton>
          <ToggleButton value="week">本周</ToggleButton>
          <ToggleButton value="month">本月</ToggleButton>
          <ToggleButton value="all">全部</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : summary ? (
        <>
          {/* 概览卡片 */}
          <SummaryCards summary={summary} />

          {/* 净值曲线 */}
          {summary.equityCurve && summary.equityCurve.length > 0 && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                净值曲线
              </Typography>
              <EquityChart data={summary.equityCurve} />
            </Paper>
          )}

          {/* 交易明细 */}
          <Paper sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ p: 2, pb: 1, fontWeight: 600 }}>
              交易明细（{summary.tradeCount} 笔）
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell>时间</TableCell>
                    <TableCell>代码</TableCell>
                    <TableCell>名称</TableCell>
                    <TableCell align="center">方向</TableCell>
                    <TableCell align="right">价格</TableCell>
                    <TableCell align="right">数量</TableCell>
                    <TableCell align="right">金额</TableCell>
                    <TableCell align="right">盈亏</TableCell>
                    <TableCell>原因</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.trades.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          暂无交易记录
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    summary.trades.map((t) => (
                      <TableRow key={t.id} hover>
                        <TableCell>{formatDateTime(t.tradeTime)}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{t.code}</TableCell>
                        <TableCell>{t.name}</TableCell>
                        <TableCell align="center">
                          <span style={{ color: t.direction === 'buy' ? '#f44336' : '#4caf50', fontWeight: 600 }}>
                            {t.direction === 'buy' ? '买入' : '卖出'}
                          </span>
                        </TableCell>
                        <TableCell align="right">{formatNumber(t.price)}</TableCell>
                        <TableCell align="right">{t.quantity}</TableCell>
                        <TableCell align="right">{formatMoney(t.amount)}</TableCell>
                        <TableCell align="right">
                          {t.profit !== undefined ? (
                            <span style={{ color: getPriceColor(t.profit), fontWeight: 600 }}>
                              {formatMoney(t.profit)}
                              {t.profitPct !== undefined && (
                                <span style={{ fontSize: 12 }}>
                                  {' '}({formatPct(t.profitPct)})
                                </span>
                              )}
                            </span>
                          ) : (
                            '--'
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {t.reason || ''}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* 入选股票明细 */}
          {summary.stockDetails && (
            <Paper sx={{ mt: 2 }}>
              <Box sx={{ p: 2, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  入选股票明细（{summary.stockDetails.length} 只）
                </Typography>
                <ToggleButtonGroup
                  value={detailFilter}
                  exclusive
                  onChange={(_e, val) => val && setDetailFilter(val)}
                  size="small"
                >
                  <ToggleButton value="all">全部</ToggleButton>
                  <ToggleButton value="up">上涨卖出</ToggleButton>
                  <ToggleButton value="down">下跌卖出</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* 卖出盈亏总结 */}
              {summary.sellSummary && summary.stockDetails.length > 0 && (
                <Box sx={{ px: 2, pb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    label={`上涨卖出 ${summary.sellSummary.upCount} 笔 · 盈利 ${formatMoney(summary.sellSummary.upProfit)}`}
                    sx={{ bgcolor: '#ffebee', color: '#f44336', fontWeight: 600 }}
                  />
                  <Chip
                    size="small"
                    label={`下跌卖出 ${summary.sellSummary.downCount} 笔 · 亏损 ${formatMoney(Math.abs(summary.sellSummary.downProfit))}`}
                    sx={{ bgcolor: '#e8f5e9', color: '#4caf50', fontWeight: 600 }}
                  />
                  {summary.sellSummary.manualCount > 0 && (
                    <Chip
                      size="small"
                      label={`手动卖出 ${summary.sellSummary.manualCount} 笔 · 盈亏 ${formatMoney(summary.sellSummary.manualProfit)}`}
                      sx={{ bgcolor: 'grey.100', color: 'text.secondary', fontWeight: 600 }}
                    />
                  )}
                </Box>
              )}

              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 1300 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 600 }}>代码</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>名称</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>买入日期</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>买入价</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>入选收盘价</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>卖出日期</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>卖出价</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>卖出类型</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>盈亏额</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>盈亏%</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>今日行情</TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 220 }}>当时入选条件</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredDetails.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            暂无入选股票明细
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredDetails.map((d) => (
                        <TableRow key={d.id} hover>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{d.code}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{d.displayName || d.name}</TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{d.buyDate}</TableCell>
                          <TableCell align="right">{formatNumber(d.buyPrice)}</TableCell>
                          <TableCell align="right">
                            {d.candidatePrice !== undefined ? (
                              <span title={d.candidateDate ? `入选日 ${d.candidateDate}` : ''}>
                                {formatNumber(d.candidatePrice)}
                              </span>
                            ) : '--'}
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{d.sellDate || '--'}</TableCell>
                          <TableCell align="right">{formatNumber(d.sellPrice)}</TableCell>
                          <TableCell align="center">
                            {d.sellType === 'up' && (
                              <Chip size="small" label="上涨卖出" sx={{ bgcolor: '#ffebee', color: '#f44336', fontWeight: 600 }} />
                            )}
                            {d.sellType === 'down' && (
                              <Chip size="small" label="下跌卖出" sx={{ bgcolor: '#e8f5e9', color: '#4caf50', fontWeight: 600 }} />
                            )}
                            {d.sellType === 'manual' && (
                              <Chip size="small" label="手动卖出" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {d.profit !== undefined ? (
                              <span style={{ color: getPriceColor(d.profit), fontWeight: 600 }}>
                                {formatMoney(d.profit)}
                              </span>
                            ) : '--'}
                          </TableCell>
                          <TableCell align="right">
                            {d.profitPct !== undefined ? (
                              <span style={{ color: getPriceColor(d.profitPct), fontWeight: 600 }}>
                                {formatPct(d.profitPct)}
                              </span>
                            ) : '--'}
                          </TableCell>
                          {/* 今日行情 */}
                          <TableCell align="right">
                            {d.todayPrice !== undefined ? (
                              <span>
                                {formatNumber(d.todayPrice)}
                                <Typography component="span" variant="caption" sx={{ display: 'block', color: getPriceColor(d.todayChangePct ?? 0) }}>
                                  {formatPct(d.todayChangePct)}
                                </Typography>
                              </span>
                            ) : '--'}
                          </TableCell>
                          {/* 当时入选条件 */}
                          <TableCell>
                            {d.matchedRules.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                {d.matchedRules.map((r, i) => (
                                  <Typography key={i} variant="caption" color="text.secondary">
                                    • {r}
                                  </Typography>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">--</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* 每日盈亏记录 */}
          {dailyPnlList.length > 0 && (
            <Paper sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ p: 2, pb: 1, fontWeight: 600 }}>
                每日盈亏记录（近{dailyPnlList.length}天）
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell>日期</TableCell>
                      <TableCell align="center">买入</TableCell>
                      <TableCell align="center">卖出</TableCell>
                      <TableCell align="center">持仓数</TableCell>
                      <TableCell align="right">已实现盈亏</TableCell>
                      <TableCell align="right">浮动盈亏</TableCell>
                      <TableCell align="right">总资产</TableCell>
                      <TableCell align="right">收益率</TableCell>
                      <TableCell>备注</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dailyPnlList.map((d) => {
                      const initialCapital = 1000000;
                      const profitPct = ((d.totalAssets - initialCapital) / initialCapital) * 100;
                      return (
                        <TableRow key={d.id} hover>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{d.date}</TableCell>
                          <TableCell align="center">{d.buyCount > 0 ? d.buyCount : '-'}</TableCell>
                          <TableCell align="center">{d.sellCount > 0 ? d.sellCount : '-'}</TableCell>
                          <TableCell align="center">{d.heldCount}</TableCell>
                          <TableCell align="right">
                            {d.realizedProfit !== 0 ? (
                              <span style={{ color: getPriceColor(d.realizedProfit), fontWeight: 600 }}>
                                {formatMoney(d.realizedProfit)}
                              </span>
                            ) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {d.unrealizedProfit !== 0 ? (
                              <span style={{ color: getPriceColor(d.unrealizedProfit), fontWeight: 600 }}>
                                {formatMoney(d.unrealizedProfit)}
                              </span>
                            ) : '-'}
                          </TableCell>
                          <TableCell align="right">{formatMoney(d.totalAssets)}</TableCell>
                          <TableCell align="right">
                            <span style={{ color: getPriceColor(profitPct), fontWeight: 600 }}>
                              {formatPct(profitPct)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {d.note === 'auto_trade' ? '自动交易' : d.note === 'no_trade' ? '无交易' : d.note}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </>
      ) : (
        <Typography color="text.secondary">暂无数据</Typography>
      )}
    </Box>
  );
}
